import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ShoppingCart, 
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Car,
  CalendarDays
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const CustomerDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_orders: 0,
    recent_orders: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'purchased':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'purchased':
        return <Package className="h-4 w-4" />;
      case 'shipped':
        return <Clock className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-slate-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-dashboard">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome, {user.full_name}!</h1>
        <p className="text-green-100">Track your vehicle orders and delivery status here.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-orders">
                  {stats.total_orders}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Active Orders</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.recent_orders.filter(order => order.status !== 'delivered').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Car className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <CardTitle>My Recent Orders</CardTitle>
          <CardDescription>Track the status of your vehicle orders</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.recent_orders.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_orders.map((order) => (
                <div key={order.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {getStatusIcon(order.status)}
                      <div>
                        <p className="font-medium text-slate-900">Order #{order.id.slice(-8).toUpperCase()}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge className={getStatusColor(order.status)}>
                            {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                          </Badge>
                          <span className="text-xs text-slate-500">•</span>
                          <span className="text-xs text-slate-500">
                            {new Date(order.order_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        ¥{order.total_amount_jpy?.toLocaleString() || 0}
                      </p>
                      <p className="text-sm text-slate-500">
                        ${order.total_amount_usd?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                  
                  {order.notes && (
                    <p className="text-sm text-slate-600 mt-3 pl-8">{order.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Orders Yet</h3>
              <p className="text-slate-500 mb-6">You haven't placed any orders yet. Contact our team to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Status Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Order Status Guide</CardTitle>
          <CardDescription>Understanding your order progress</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Package className="h-5 w-5 text-yellow-600" />
                <Badge className="bg-yellow-100 text-yellow-800">Purchased</Badge>
              </div>
              <p className="text-sm text-yellow-800">
                Your vehicle has been purchased from the auction and is being prepared for shipping.
              </p>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <Badge className="bg-blue-100 text-blue-800">Shipped</Badge>
              </div>
              <p className="text-sm text-blue-800">
                Your vehicle is currently in transit and on its way to the destination port.
              </p>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <Badge className="bg-green-100 text-green-800">Delivered</Badge>
              </div>
              <p className="text-sm text-green-800">
                Your vehicle has arrived at the destination and is ready for pickup or final delivery.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
          <CardDescription>Contact our team for assistance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Customer Support</h4>
              <div className="space-y-2 text-sm text-slate-600">
                <p>📧 support@globaltradecar.com</p>
                <p>📞 +81 70-9270-2242</p>
                <p>🕒 Mon-Fri: 9:00 AM - 6:00 PM</p>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Sales Team</h4>
              <div className="space-y-2 text-sm text-slate-600">
                <p>📧 sales@globaltradecar.com</p>
                <p>📞 +81 70-9270-2242</p>
                <p>🕒 Mon-Sat: 8:00 AM - 8:00 PM</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerDashboard;